import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type ChangeEvent,
  type KeyboardEvent,
  type SyntheticEvent,
} from 'react'
import {Box, Slider, Typography} from '@mui/material'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── SliderRow — label above, slider + number input below ──────────

interface SliderRowProps {
  label: string
  value: number
  min: number
  max: number
  step: number
  unit: string
  onChange: (v: number) => void
  /** When provided, slider uses local state during drag and only calls this on release */
  onChangeCommitted?: (v: number) => void
}

const SliderRow = ({label, value, min, max, step, unit, onChange, onChangeCommitted}: SliderRowProps) => {
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const [localValue, setLocalValue] = useState<number | null>(null)
  const [inputText, setInputText] = useState<string | null>(null)

  const displayValue = localValue ?? value

  const handleSliderChange = useCallback(
    (_: Event, v: number | number[]) => {
      const val = v as number
      if (onChangeCommitted) {
        setLocalValue(val)
        onChange(val)
      } else {
        onChange(val)
      }
    },
    [onChange, onChangeCommitted],
  )

  const handleSliderCommitted = useCallback(
    (_: Event | SyntheticEvent, v: number | number[]) => {
      if (onChangeCommitted) {
        setLocalValue(null)
        onChangeCommitted(v as number)
      }
    },
    [onChangeCommitted],
  )

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value)
  }, [])

  const commitInputValue = useCallback(() => {
    if (inputText === null) return
    const parsed = parseFloat(inputText)
    setInputText(null)
    if (Number.isNaN(parsed)) return
    const clamped = Math.min(max, Math.max(min, parsed))
    const rounded = Math.round(clamped / step) * step
    // Mirror the drag path: apply the value via onChange, then commit/seal via
    // onChangeCommitted. Calling only onChangeCommitted dropped the typed value for
    // rows whose onChangeCommitted only seals undo (e.g. material sliders).
    onChange(rounded)
    onChangeCommitted?.(rounded)
  }, [inputText, onChange, onChangeCommitted, min, max, step])

  const handleInputBlur = useCallback(() => {
    commitInputValue()
  }, [commitInputValue])

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        commitInputValue()
        ;(e.target as HTMLInputElement).blur()
      }
    },
    [commitInputValue],
  )

  return (
    <Box sx={sx.root}>
      <Typography sx={sx.label}>{label}</Typography>
      <Box sx={sx.sliderRow}>
        <Slider
          size="small"
          value={displayValue}
          min={min}
          max={max}
          step={step}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommitted}
          sx={sx.slider}
        />
        <Box sx={sx.inputWrapper}>
          <input
            type="number"
            value={inputText ?? displayValue}
            min={min}
            max={max}
            step={step}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            style={sx.input}
          />
          <Typography sx={sx.unit}>{unit}</Typography>
        </Box>
      </Box>
    </Box>
  )
}

// ── Styles ─────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  label: {
    fontSize: 11,
    color: tokens.text.muted,
    fontWeight: 500,
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  slider: {
    flex: 1,
    '& .MuiSlider-thumb': {
      width: 14,
      height: 14,
    },
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0,
  },
  input: {
    width: 52,
    height: 24,
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: 4,
    backgroundColor: 'transparent',
    color: tokens.text.primary,
    fontSize: 11,
    textAlign: 'right' as const,
    padding: '0 4px',
    outline: 'none',
  } as CSSProperties,
  unit: {
    fontSize: 10,
    color: tokens.text.muted,
    minWidth: 18,
  },
})

export default SliderRow
